import React, { FC } from 'react';
import { FaCheckCircle } from '@react-icons/all-files/fa/FaCheckCircle';
import { FaRegTimesCircle } from '@react-icons/all-files/fa/FaRegTimesCircle';
import { IoMdWarning } from '@react-icons/all-files/io/IoMdWarning';
import { useTranslations } from '../../../../hooks/useTranslationsHelper';
import { ActionPanel } from '../../../shared/ActionPanel';
import { Button } from '../../../shared/Button';
import { PopUpHandler } from '../../../shared/PopupHandler';
import { PurchaseModalState } from '../types';
import { goToLink } from '../../../../utils/links-helper/links-helper';
import { useNavigate } from 'react-router-dom';
import { StarIcon } from '../../../shared/SVG/Icons/StarIcon';
import { joinStrings } from '../../../../utils/string';
import './PurchaseModal.scss';

export type PurchaseModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Current display state of the modal */
  state: PurchaseModalState;
  /** Item title – comes pre-translated from parent (e.g. "50 FREE SPINS") */
  itemTitle?: string | null;
  /** Item subtitle – comes pre-translated from parent (e.g. "On Big Bass Splash!") */
  itemSubtitle?: string | null;
  /** Game name – comes pre-translated from parent (e.g. "Big Bass Splash") */
  itemGameName?: string | null;
  /** User's current star balance */
  currentBalance?: number | null;
  /** Cost of the item in stars */
  cost?: string | null;
  /** Called when user confirms the purchase */
  onConfirm?: () => void;
  confirmationCTAUrl?: string | null;
  confirmationCTACopy?: string | null;
};

export const PurchaseModal: FC<PurchaseModalProps> = ({
  isOpen,
  onClose,
  state,
  itemTitle,
  itemSubtitle,
  itemGameName,
  currentBalance,
  cost,
  onConfirm,
  confirmationCTAUrl,
  confirmationCTACopy,
}) => {
  const { t } = useTranslations();
  const navigate = useNavigate();

  const newBalance = Number(currentBalance) - Number(cost);
  const starsNeeded = Number(cost) - Number(currentBalance);

  const isInsufficient = state === 'insufficient';
  const isSuccess = state === 'success';
  const isError = state === 'error';

  const goToConfirmationUrl = (): void => {
    confirmationCTAUrl && goToLink(confirmationCTAUrl, navigate, '_blank');
  };

  const reviewFooter = (
    <ActionPanel align="center" inline>
      <Button type="button" variant="secondary" onClick={onClose} className="purchase-modal__btn">
        {t('buttons.cancel.uppercase')}
      </Button>
      <Button
        type="button"
        variant="primary"
        onClick={onConfirm}
        disabled={isInsufficient}
        className="purchase-modal__btn"
      >
        {t('shop.purchase-modal.confirm-text')}
      </Button>
    </ActionPanel>
  );

  const successFooter = (
    <ActionPanel align="center" inline>
      <Button type="button" variant="secondary" onClick={onClose} className="purchase-modal__btn">
        {t('buttons.close')}
      </Button>
      <Button type="button" variant="primary" onClick={goToConfirmationUrl} className="purchase-modal__btn">
        {confirmationCTACopy}
      </Button>
    </ActionPanel>
  );

  const itemCard = (
    <div className="purchase-modal__item-card">
      <p className="purchase-modal__item-title">{itemTitle}</p>
      <p className="purchase-modal__item-subtitle">{itemSubtitle}</p>
      <p className="purchase-modal__item-game">{itemGameName}</p>
    </div>
  );

  const balanceRow = (label: string, value: number, modifier?: 'highlight' | 'danger'): JSX.Element => (
    <div
      className={joinStrings([
        'purchase-modal__balance-row',
        modifier === 'highlight' && 'purchase-modal__balance-row--new-balance',
      ])}
    >
      <span className="purchase-modal__balance-label">{label}</span>
      <div className="purchase-modal__balance-value">
        <span className={`${modifier ? ` purchase-modal__balance-value--${modifier}` : ''}`}>{value} </span>
        <StarIcon />
      </div>
    </div>
  );

  if (isSuccess) {
    return (
      <PopUpHandler
        isOpen={isOpen}
        onClose={onClose}
        footer={successFooter}
        showTouchPad={false}
        modalProps={{ className: 'purchase-modal--dialog' }}
        drawerProps={{ className: 'purchase-modal--drawer' }}
      >
        <div className="purchase-modal__success">
          <FaCheckCircle className="purchase-modal__success-icon" aria-hidden="true" />
          <p className="purchase-modal__success-title">{t('shop.purchase-modal.success-title')}</p>
          <p className="purchase-modal__success-subtitle">{t('shop.purchase-modal.success-subtitle')}</p>
        </div>
      </PopUpHandler>
    );
  }

  return (
    <PopUpHandler
      isOpen={isOpen}
      onClose={onClose}
      header={t('shop.purchase-modal.review-title')}
      footer={reviewFooter}
      showTouchPad={false}
      modalProps={{ className: 'purchase-modal--dialog' }}
      drawerProps={{ className: 'purchase-modal--drawer' }}
    >
      <div className="purchase-modal__body">
        {itemCard}

        <div className="purchase-modal__balance-rows">
          {balanceRow(
            t('shop.purchase-modal.current-balance'),
            Number(currentBalance),
            isInsufficient ? 'danger' : undefined
          )}
          {balanceRow(t('shop.purchase-modal.cost'), Number(cost))}
          <hr className="purchase-modal__devider" />
          {!isInsufficient && balanceRow(t('shop.purchase-modal.new-balance'), newBalance, 'highlight')}
        </div>

        {isInsufficient && (
          <div className="purchase-modal__alert purchase-modal__alert--danger" role="alert">
            <FaRegTimesCircle className="purchase-modal__alert-icon" aria-hidden="true" />
            <div>
              <p className="purchase-modal__alert-title">{t('shop.purchase-modal.insufficient-title')}</p>
              <p className="purchase-modal__alert-message">
                {t('shop.purchase-modal.insufficient-message', { count: starsNeeded })}
              </p>
            </div>
          </div>
        )}

        {isError && (
          <div className="purchase-modal__alert purchase-modal__alert--warning" role="alert">
            <IoMdWarning className="purchase-modal__alert-icon" aria-hidden="true" />
            <div>
              <p className="purchase-modal__alert-title">{t('shop.purchase-modal.error-title')}</p>
              <p className="purchase-modal__alert-message">{t('shop.purchase-modal.error-message')}</p>
            </div>
          </div>
        )}
      </div>
    </PopUpHandler>
  );
};
